import React, { useState } from 'react';
import styled from 'styled-components';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { colors } from '../styles/GlobalStyles';
import { configService } from '../services/configService';

const PayPalContainer = styled.div`
  background: ${colors.white};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.secondary};
  margin: 5px 0;
  box-shadow: none;
  
  @media (min-width: 768px) {
    padding: 30px;
    margin: 20px 0;
    border: 2px solid ${colors.secondary};
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const PayPalTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 15px;
  text-align: center;
  font-size: 1.2rem;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    margin-bottom: 20px;
    font-size: 1.5rem;
  }
`;

const PaymentSummary = styled.div`
  background: transparent;
  padding: 6px 0;
  border-radius: 0;
  margin-bottom: 8px;
  border: none;
  box-shadow: none;
  
  @media (min-width: 768px) {
    background: ${colors.cream};
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid ${colors.secondary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
  padding: 1px 4px;
  gap: 8px;
  min-height: 18px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 4px;
    border-top: 1px solid ${colors.primary};
    font-weight: bold;
    margin-top: 2px;
  }
  
  @media (min-width: 768px) {
    margin-bottom: 10px;
    padding: 5px 0;
    gap: 15px;
    
    &:last-child {
      padding-top: 10px;
      margin-top: 6px;
      border-top: 2px solid ${colors.primary};
    }
  }
`;

const SummaryLabel = styled.span`
  color: ${colors.brown};
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  font-size: 0.9rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.3;
  }
`;

const SummaryValue = styled.span`
  color: ${colors.primary};
  font-weight: 600;
  text-align: right;
  flex-shrink: 0;
  min-width: 50px;
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.3;
    min-width: auto;
  }
`;

const PayPalInfo = styled.p`
  color: ${colors.brown};
  font-size: 0.8rem;
  text-align: center;
  margin-bottom: 12px;
  line-height: 1.3;
  padding: 0 4px;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 20px;
    padding: 0;
    line-height: 1.4;
  }
`;

const PaymentTypeSelector = styled.div`
  background: transparent;
  padding: 0;
  border-radius: 0;
  margin-bottom: 6px;
  border: none;
  
  @media (min-width: 768px) {
    background: ${colors.cream};
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 2px solid ${colors.secondary};
  }
`;

const PaymentOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 8px;
  margin-bottom: 6px;
  background: ${colors.cream};
  border: 1px solid ${props => props.$checked ? colors.primary : 'transparent'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 8px;
  
  &:hover {
    border-color: ${colors.primary};
    box-shadow: none;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (min-width: 768px) {
    align-items: center;
    padding: 15px;
    gap: 15px;
    margin-bottom: 10px;
    border-radius: 8px;
    background: ${colors.white};
    border: 2px solid ${props => props.$checked ? colors.primary : '#e0e0e0'};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const RadioInput = styled.input`
  margin: 0;
  width: 18px;
  height: 18px;
  accent-color: ${colors.primary};
  flex-shrink: 0;
  margin-top: 2px;
  
  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-weight: bold;
  color: ${colors.primary};
  margin-bottom: 3px;
  font-size: 0.95rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 5px;
  }
`;

const OptionDescription = styled.div`
  color: ${colors.brown};
  font-size: 0.75rem;
  line-height: 1.3;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const OptionPrice = styled.div`
  font-weight: bold;
  color: ${colors.primary};
  font-size: 1.1rem;
  flex-shrink: 0;
  text-align: right;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PaymentSelectorTitle = styled.h3`
  margin: 0 0 8px 0;
  color: ${colors.primary};
  font-size: 1rem;
  padding: 0 4px;
  
  @media (min-width: 768px) {
    margin: 0 0 15px 0;
    font-size: 1.1rem;
    padding: 0;
  }
`;

interface PayPalPaymentProps {
  adults: number;
  children: number;
  bicchieri?: number;
  email: string;
  onPaymentSuccess: (paymentDetails: any) => void;
  onPaymentError: (error: string) => void;
}

const PREZZO_BICCHIERE = 5;

const PayPalPayment: React.FC<PayPalPaymentProps> = ({
  adults,
  children,
  bicchieri = 0,
  email,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState<'deposit' | 'full' | 'custom'>('deposit');
  const [customAmount, setCustomAmount] = useState<string>('');
  
  // Verifica se siamo in modalità development (locale)
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Funzione per controllare se è un'email di test
  const isProductionTestEmail = (email: string): boolean => {
    const testEmails = ['alberto.malberti@gmail.com'];
    return testEmails.includes(email.toLowerCase());
  };
  
  // Se è email di test, usa sempre 0.99€, altrimenti calcola normalmente
  const isTestEmail = isProductionTestEmail(email);
  
  // Calcolo prezzi base (sempre calcolati per la visualizzazione)
  const adultTotal = adults * 25;
  const childTotal = children * 15;
  const standardFullTotal = adultTotal + childTotal;
  
  // Se è email di test, usa sempre 0.99€, altrimenti calcola normalmente
  let totalDeposit: number;
  let fullTotal: number;
  
  if (isTestEmail) {
    console.log('🧪 [TEST] Email di test rilevata, importo PayPal ridotto a €0.99');
    totalDeposit = 0.99;
    fullTotal = 0.99;
  } else {
    totalDeposit = (adults + children) * 5;
    fullTotal = standardFullTotal;
  }
  
  const remaining = fullTotal - totalDeposit;

  // Il bicchiere commemorativo si paga sempre per intero, indipendentemente da acconto/completo
  const bicchieriTotal = bicchieri * PREZZO_BICCHIERE;

  // Importo da pagare basato sulla scelta dell'utente
  let amountToPay: number;
  if (paymentType === 'custom' && customAmount) {
    amountToPay = parseFloat(customAmount);
  } else {
    amountToPay = (paymentType === 'full' ? fullTotal : totalDeposit) + bicchieriTotal;
  }

  // Funzione per rilevare se siamo su mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Funzione per rilevare il tipo di dispositivo mobile specifico
  const getMobileOS = () => {
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
    if (/Android/.test(userAgent)) return 'android';
    return 'other';
  };

  const createOrder = (data: any, actions: any) => {
    const bicchieriSuffix = bicchieri > 0 ? `, ${bicchieri} bicchieri commemorativi` : '';
    let description: string;
    if (paymentType === 'custom') {
      description = `Acconto personalizzato C'è Aria di Fest - ${adults} adulti, ${children} bambini${bicchieriSuffix} - €${amountToPay}`;
    } else {
      description = paymentType === 'full'
        ? `Pagamento completo C'è Aria di Fest - ${adults} adulti, ${children} bambini${bicchieriSuffix}`
        : `Acconto prenotazione C'è Aria di Fest - ${adults} adulti, ${children} bambini${bicchieriSuffix}`;
    }

    return actions.order.create({
      purchase_units: [
        {
          description: description,
          amount: {
            currency_code: "EUR",
            value: amountToPay.toFixed(2),
          },
        },
      ],
      application_context: {
        brand_name: "C'è Aria di Fest",
        user_action: "PAY_NOW",
        shipping_preference: "NO_SHIPPING",
        // Abilita In-Context Checkout (popup invece di redirect)
        landing_page: "NO_PREFERENCE",
        payment_method: {
          payer_selected: "PAYPAL",
          payee_preferred: "IMMEDIATE_PAYMENT_REQUIRED"
        }
      }
    });
  };

  const onApprove = async (data: any, actions: any) => {
    setIsProcessing(true);
    try {
      const order = await actions.order.capture();
      
      const paymentDetails = {
        orderId: order.id,
        payerId: order.payer.payer_id,
        payerEmail: order.payer.email_address,
        payerName: `${order.payer.name.given_name} ${order.payer.name.surname}`,
        amount: amountToPay,
        currency: 'EUR',
        status: order.status,
        timestamp: new Date().toISOString(),
        paymentType: paymentType, // 'deposit' o 'full'
        isFullPayment: paymentType === 'full'
      };
      
      onPaymentSuccess(paymentDetails);
    } catch (error) {
      console.error('❌ Errore nel completamento del pagamento:', error);
      onPaymentError('Errore nel completamento del pagamento. Riprova.');
    } finally {
      setIsProcessing(false);
    }
  };

  const onError = (err: any) => {
    console.error('❌ Errore PayPal:', err);
    onPaymentError('Si è verificato un errore con PayPal. Riprova.');
    setIsProcessing(false);
  };

  const onCancel = (data: any) => {
    onPaymentError('Pagamento annullato.');
    setIsProcessing(false);
  };

  return (
    <PayPalContainer>
      <PayPalTitle>💳 Pagamento con PayPal</PayPalTitle>
      
      <PaymentTypeSelector>
        <PaymentSelectorTitle>💰 Scegli il tipo di pagamento:</PaymentSelectorTitle>
        
        <PaymentOption $checked={paymentType === 'deposit'}>
          <RadioInput 
            type="radio" 
            name="paymentType" 
            value="deposit" 
            checked={paymentType === 'deposit'}
            onChange={(e) => setPaymentType(e.target.value as 'deposit' | 'full' | 'custom')}
          />
          <OptionContent>
            <OptionTitle>🎯 Acconto</OptionTitle>
            <OptionDescription>
              Paga solo l'acconto ora, il resto durante l'evento.
            </OptionDescription>
          </OptionContent>
          <OptionPrice>€{totalDeposit}</OptionPrice>
        </PaymentOption>

        <PaymentOption $checked={paymentType === 'full'}>
          <RadioInput 
            type="radio" 
            name="paymentType" 
            value="full" 
            checked={paymentType === 'full'}
            onChange={(e) => setPaymentType(e.target.value as 'deposit' | 'full' | 'custom')}
          />
          <OptionContent>
            <OptionTitle>✅ Pagamento Completo</OptionTitle>
            <OptionDescription>
              Paga l'intero importo ora ed evita l'eventuale coda al tuo arrivo
            </OptionDescription>
          </OptionContent>
          <OptionPrice>€{fullTotal}</OptionPrice>
        </PaymentOption>

        {isDevelopment && (
          <PaymentOption $checked={paymentType === 'custom'}>
            <RadioInput 
              type="radio" 
              name="paymentType" 
              value="custom" 
              checked={paymentType === 'custom'}
              onChange={(e) => setPaymentType(e.target.value as 'deposit' | 'full' | 'custom')}
            />
            <OptionContent>
              <OptionTitle>🧪 Acconto Personalizzato (TEST)</OptionTitle>
              <OptionDescription>
                Solo in modalità locale - Inserisci un importo personalizzato per il test.
              </OptionDescription>
              {paymentType === 'custom' && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.50"
                    max={fullTotal}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Inserisci importo (€)"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
            </OptionContent>
            {paymentType === 'custom' && customAmount && (
              <OptionPrice>€{parseFloat(customAmount).toFixed(2)}</OptionPrice>
            )}
          </PaymentOption>
        )}
      </PaymentTypeSelector>
      
      <PaymentSummary>
        <SummaryItem>
          <SummaryLabel>Menù Adulto/Vegetariano ({adults}x):</SummaryLabel>
          <SummaryValue>
            {paymentType === 'full' 
              ? `€${adultTotal} (totale)` 
              : `€${adults * 5} (acconto) di €${adultTotal} totali`
            }
          </SummaryValue>
        </SummaryItem>
        {children > 0 && (
          <SummaryItem>
            <SummaryLabel>Bambini ({children}x):</SummaryLabel>
            <SummaryValue>
              {paymentType === 'full'
                ? `€${childTotal} (totale)`
                : `€${children * 5} (acconto) di €${childTotal} totali`
              }
            </SummaryValue>
          </SummaryItem>
        )}
        {bicchieri > 0 && (
          <SummaryItem>
            <SummaryLabel>Bicchieri commemorativi ({bicchieri}x):</SummaryLabel>
            <SummaryValue>€{bicchieriTotal} (pagati per intero)</SummaryValue>
          </SummaryItem>
        )}
        <SummaryItem>
          <SummaryLabel>
            {paymentType === 'full' ? 'Totale da pagare ora:' : 'Totale acconto da pagare ora:'}
          </SummaryLabel>
          <SummaryValue>€{amountToPay}</SummaryValue>
        </SummaryItem>
        {paymentType === 'deposit' && (
          <SummaryItem style={{ fontSize: '0.9rem', fontWeight: 'normal', borderTop: 'none', paddingTop: '5px' }}>
            <SummaryLabel>Saldo rimanente (da pagare all'evento):</SummaryLabel>
            <SummaryValue>€{remaining}</SummaryValue>
          </SummaryItem>
        )}
      </PaymentSummary>

      <PayPalInfo>
        <strong>- Se paghi con carta, PayPal purtroppo non accetta alcune carte ricaricabili -</strong><br />
        {paymentType === 'full'
          ? `Stai per pagare l'intero importo di €${amountToPay}. Non dovrai pagare nulla all'evento!`
          : paymentType === 'custom'
          ? `Stai pagando un acconto personalizzato di €${amountToPay}. Il saldo rimanente di €${(fullTotal - amountToPay).toFixed(2)} sarà da versare durante l'evento.`
          : `Stai pagando un acconto di €${amountToPay}. Il saldo rimanente di €${remaining} sarà da versare durante l'evento.`
        }<br />
       
        Dopo il pagamento riceverai conferma via email con tutti i dettagli.
      </PayPalInfo>

      <PayPalScriptProvider 
        options={{ 
          clientId: configService.getPayPalClientId(),
          currency: "EUR",
          intent: "capture",
          // Ottimizzazioni per mobile (non deep linking automatico)
         
          // Configurazioni per migliorare l'esperienza mobile
         
        }}
      >
        <PayPalButtons
          style={{ 
            layout: "vertical", 
            color: "blue", 
            height: isMobile() ? 45 : 50, 
            borderRadius: 6,
            // Su mobile, rende i bottoni più adatti al touch
            ...(isMobile() && {
              shape: "rect",
              tagline: false
            })
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onError}
          onCancel={onCancel}
          disabled={isProcessing || (paymentType === 'custom' && (!customAmount || parseFloat(customAmount) < 0.5))}
          // Re-render dinamico per aggiornamenti
          forceReRender={[amountToPay, paymentType, customAmount]}
        />
      </PayPalScriptProvider>
      
      {paymentType === 'custom' && (!customAmount || parseFloat(customAmount) < 0.5) && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '15px', 
          color: '#721c24',
          fontWeight: 'bold',
          background: '#f8d7da',
          padding: '15px',
          borderRadius: '8px',
          border: '2px solid #f5c6cb'
        }}>
          ⚠️ Inserisci un importo valido (minimo €0.50)
        </div>
      )}
      
      {isProcessing && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '15px', 
          color: colors.primary,
          fontWeight: 'bold',
          background: colors.cream,
          padding: '15px',
          borderRadius: '8px',
          border: `2px solid ${colors.secondary}`
        }}>
          🔄 Elaborazione pagamento PayPal in corso...<br />
          <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: colors.brown }}>
            {paymentType === 'full'
              ? `Pagamento completo di €${amountToPay} in elaborazione...`
              : paymentType === 'custom'
              ? `Acconto personalizzato di €${amountToPay} in elaborazione...`
              : `Acconto di €${amountToPay} in elaborazione...`
            }<br />
            Non chiudere questa pagina
          </span>
        </div>
      )}
    </PayPalContainer>
  );
};

export default PayPalPayment;
